import React, { forwardRef, useId, useState } from 'react';
import { Typography } from '../Typography';
import styles from './Checkbox.module.scss';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'onChange'> {
  label?: React.ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  className?: string;
  labelClassName?: string;
  error?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      checked,
      defaultChecked = false,
      onChange,
      disabled = false,
      size = 'md',
      color = 'primary',
      className = '',
      labelClassName = '',
      error,
      ...props
    },
    ref
  ) => {
    const [isChecked, setIsChecked] = useState(defaultChecked);
    const [isFocused, setIsFocused] = useState(false);
    // Stable across server/client render (unlike Math.random(), which caused
    // a hydration mismatch and, since it was called separately for the input
    // and the label, didn't even match its own htmlFor within one render).
    const generatedId = useId();
    const inputId = props.id || `checkbox-${generatedId}`;

    const currentChecked = checked !== undefined ? checked : isChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;

      if (checked === undefined) {
        setIsChecked(newChecked);
      }

      onChange?.(newChecked);
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const checkboxClasses = [
      styles.checkbox,
      styles[`size-${size}`],
      styles[`color-${color}`],
      currentChecked && styles.checked,
      disabled && styles.disabled,
      isFocused && styles.focused,
      error && styles.error,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const labelClasses = [
      styles.label,
      disabled && styles.labelDisabled,
      error && styles.labelError,
      labelClassName,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`${styles.checkboxContainer} ${error ? styles.error : ''}`}>
        <label className={checkboxClasses}>
          <input
            ref={ref}
            type="checkbox"
            checked={currentChecked}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            className={styles.input}
            {...props}
            id={inputId}
          />

          <div
            className={styles.checkboxBox}
            style={error ? { borderColor: 'var(--color-error)' } : {}}
          >
            {currentChecked && (
              <svg
                className={styles.checkmark}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M10 3L4.5 8.5L2 6"
                  stroke="#FFFFFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </label>

        {label && (
          <label htmlFor={inputId} className={labelClasses}>
            <Typography
              variant="text-sm"
              as="span"
              color={disabled ? 'disabled' : error ? 'error' : 'secondary'}
              weight="normal"
            >
              {label}
            </Typography>
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
